import React, { useMemo } from 'react';
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { useSelector } from 'react-redux';
import RouterComponent from '../Router/Router.component';
import { RootState, useAppDispatch } from '../../redux/store';
import { createAppTheme } from '../../styles';
import { CssVarsProvider } from '../../styles/cssVarsProvider';
import { getSystemTheme, setTheme } from '../../redux/reducers/theme-reducer';
import { fetchAvailableThemes, fetchCurrentTheme } from '../../redux/reducers/themeEditorReducer';
import { getValidAccessToken } from '../../api/auth';
import { getTokens } from '../../utils/authStorage';
import { fetchUserInfo, userLoggedIn } from '../../redux/reducers/authInfoReducer';

export const THEME_STORAGE_KEY = 'app.theme';

const ThemeProvider = () => {
  const dispatch = useAppDispatch();
  const { mode, config, baseMode } = useSelector((state: RootState) => state.theme);
  const { authorized, userInfo } = useSelector((state: RootState) => state.authInfo);

  // Создаем тему на основе базового режима и кастомного конфига (если он есть)
  const theme = useMemo(() => {
    // Конфиг применяется только если выбран режим 'custom'
    const effectiveConfig = mode === 'custom' ? config : null;
    return createAppTheme(baseMode, effectiveConfig);
  }, [mode, config, baseMode]);

  // Следим за изменениями системной темы
  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const newSystemTheme = getSystemTheme();
      // Если текущий режим 'system', обновляем baseMode в Redux
      if (mode === 'system') {
        dispatch(setTheme({ baseMode: newSystemTheme }));
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [dispatch, mode]);

  React.useEffect(() => {
    if (!authorized) return;
    dispatch(fetchAvailableThemes());
    dispatch(fetchCurrentTheme());
  }, [authorized]);

  React.useEffect(() => {
    const initAuth = async () => {
      try {
        const token = await getValidAccessToken();
        const tokens = getTokens();
        if (token && tokens?.access_token) {
          dispatch(userLoggedIn());
          await dispatch(fetchUserInfo(tokens.access_token));
        }
      } catch (e) {
        console.error('Auth init error', e);
      }
    };

    initAuth();
  }, [authorized]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <CssVarsProvider />
      <RouterComponent />
    </MuiThemeProvider>
  );
};

export default ThemeProvider;
